import React, { useRef, useMemo, useCallback, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ImageResize } from "quill-image-resize-module-ts";

Quill.register("modules/imageResize", ImageResize);

const Size = Quill.import("attributors/style/size");
Size.whitelist = ["10px", "12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "36px", "48px"];
Quill.register(Size, true);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const TOOLTIPS = {
  "ql-bold":        "Tebal (Bold)",
  "ql-italic":      "Miring (Italic)",
  "ql-underline":   "Garis bawah (Underline)",
  "ql-strike":      "Coret (Strikethrough)",
  "ql-color":       "Warna teks",
  "ql-background":  "Warna latar",
  "ql-align":       "Rata teks",
  "ql-list[value=ordered]":  "Daftar bernomor",
  "ql-list[value=bullet]":   "Daftar poin",
  "ql-indent[value=-1]":     "Kurangi indentasi",
  "ql-indent[value=+1]":     "Tambah indentasi",
  "ql-blockquote":  "Kutipan (Blockquote)",
  "ql-code-block":  "Blok kode",
  "ql-link":        "Sisipkan tautan",
  "ql-image":       "Sisipkan gambar",
  "ql-clean":       "Hapus format",
  "ql-header":      "Gaya teks (Heading)",
  "ql-size":        "Ukuran font",
};

function injectTooltips(container) {
  if (!container) return;
  Object.entries(TOOLTIPS).forEach(([selector, label]) => {
    const attrMatch = selector.match(/^([\w-]+)\[(\w+)=(.+)\]$/);
    let el;
    if (attrMatch) {
      const [, cls, attr, val] = attrMatch;
      el = container.querySelector(`.${cls}[${attr}="${val}"]`);
    } else {
      el = container.querySelector(`.${selector}`);
    }
    if (el) {
      el.removeAttribute("title");
      el.setAttribute("data-tooltip", label);
    }
  });
  const headerPicker = container.querySelector(".ql-header .ql-picker-label");
  if (headerPicker) { headerPicker.removeAttribute("title"); headerPicker.setAttribute("data-tooltip", "Gaya teks"); }
  const sizePicker = container.querySelector(".ql-size .ql-picker-label");
  if (sizePicker) { sizePicker.removeAttribute("title"); sizePicker.setAttribute("data-tooltip", "Ukuran font"); }
}

export default function RichTextEditor({ value, onChange, placeholder, testId }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (wrapRef.current) injectTooltips(wrapRef.current);
  }, []);

  const [selectedImg, setSelectedImg] = React.useState(null);

  useEffect(() => {
    const editor = ref.current?.getEditor();
    if (!editor) return;
    const handleClick = (e) => {
      const img = e.target.closest ? e.target : null;
      if (img && img.tagName === "IMG") setSelectedImg(img);
      else setSelectedImg(null);
    };
    // also detect when imageResize overlay appears (user clicked image through overlay)
    const handleMouseDown = () => {
      setTimeout(() => {
        const qlContainer = wrapRef.current?.querySelector(".ql-container");
        const overlay = qlContainer?.querySelector("div:not([class])");
        if (overlay) {
          const overlayRect = overlay.getBoundingClientRect();
          const imgs = editor.root.querySelectorAll("img");
          let found = null;
          imgs.forEach((img) => {
            const r = img.getBoundingClientRect();
            if (Math.abs(r.left - overlayRect.left) < 20 && Math.abs(r.top - overlayRect.top) < 20) found = img;
          });
          setSelectedImg(found || imgs[0] || null);
        } else {
          setSelectedImg(null);
        }
      }, 100);
    };
    editor.root.addEventListener("click", handleClick);
    wrapRef.current?.addEventListener("mousedown", handleMouseDown);
    return () => {
      editor.root.removeEventListener("click", handleClick);
      wrapRef.current?.removeEventListener("mousedown", handleMouseDown);
    };
  }, []);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { alert("Ukuran gambar maksimal 5MB"); return; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const resp = await fetch(`${BACKEND_URL}/api/admin/upload`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        const data = await resp.json();
        const url = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
        const editor = ref.current?.getEditor();
        if (editor) {
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, "image", url);
          editor.setSelection(range.index + 1);
        }
      } catch (e) {
        alert("Gagal upload gambar");
      }
    };
  }, []);

  const deleteSelectedImage = useCallback(() => {
    const editor = ref.current?.getEditor();
    if (!editor) return;
    const selected = editor.root.querySelector("img.selected, img[data-selected]");
    if (selected) {
      const blot = Quill.find(selected);
      if (blot) { blot.remove(); return; }
    }
    // fallback: hapus via selection range
    const range = editor.getSelection();
    if (range && range.length === 0) {
      const [leaf] = editor.getLeaf(range.index);
      if (leaf && leaf.domNode && leaf.domNode.tagName === "IMG") {
        const index = editor.getIndex(leaf);
        editor.deleteText(index, 1);
      }
    }
  }, []);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ["10px", "12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "36px", "48px"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: ["#1A1422", "#FF8C00", "#6B21A8", "#EF4444", "#10B981", "#F59E0B", "#3B82F6", "#5C5868", "#FFFFFF"] }, { background: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        ["blockquote", "code-block"],
        ["link", "image"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
      handleStyles: { backgroundColor: "#FF8C00", border: "none", color: "white" },
    },
    keyboard: {
      bindings: {
        deleteImage: {
          key: ["Delete", "Backspace"],
          handler(range) {
            const editor = ref.current?.getEditor();
            if (!editor) return true;
            const [leaf] = editor.getLeaf(range.index);
            if (leaf && leaf.domNode && leaf.domNode.tagName === "IMG") {
              const index = editor.getIndex(leaf);
              editor.deleteText(index, 1);
              return false;
            }
            // also check if image is selected via imageResize
            const selected = editor.root.querySelector("img.selected, img[data-resize]");
            if (selected) {
              const blot = Quill.find(selected);
              if (blot) { blot.remove(); return false; }
            }
            return true;
          },
        },
      },
    },
    clipboard: { matchVisual: false },
  }), [imageHandler]);

  const formats = [
    "header", "size",
    "bold", "italic", "underline", "strike",
    "color", "background",
    "align",
    "list", "indent",
    "blockquote", "code-block",
    "link", "image",
  ];

  const handleDeleteImg = () => {
    if (!selectedImg) return;
    const editor = ref.current?.getEditor();
    if (!editor) return;
    const blot = Quill.find(selectedImg);
    if (blot) blot.remove();
    setSelectedImg(null);
  };

  return (
    <div className="bisa-quill" ref={wrapRef} data-testid={testId}>
      {selectedImg && (
        <button
          onMouseDown={(e) => { e.preventDefault(); handleDeleteImg(); }}
          style={{
            display: "block", marginBottom: "4px",
            background: "#EF4444", color: "#fff", border: "none",
            borderRadius: "6px", padding: "4px 12px", fontSize: "12px",
            fontWeight: "700", cursor: "pointer",
          }}
        >
          🗑 Hapus Gambar Terpilih
        </button>
      )}
      <ReactQuill
        ref={ref}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || "Tulis materi di sini..."}
      />
    </div>
  );
}
